import React, { useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, FileText, Files, Loader2, Sparkles } from 'lucide-react';
import LogicTree from './LogicTree';

// Pre-session "launchpad" for an intake. Clicking a type card lands here
// instead of immediately creating a session: it sets expectations (what gets
// produced, what you'll be asked) and lets the user paste context for AI
// prefill up front. The session is created only when they hit Start.
export default function GenerateLaunchpad({ type, onStart, onCancel, starting }) {
  const [notes, setNotes] = useState('');
  // Expanded by default — surfacing the questionnaire (and, for screening
  // bundles, the decision chart) before starting is the whole point of this
  // step. Collapsible for when you just want to start.
  const [showQuestions, setShowQuestions] = useState(true);

  const isBundle = !!type.bundle;
  const Icon = isBundle ? Files : FileText;
  const sections = type.previewSections || [];
  const totalQuestions = sections.reduce((n, s) => n + (s.questions?.length || 0), 0) || type.questionCount || 0;
  const estMins = Math.max(2, Math.round((type.questionCount || totalQuestions || 6) * 0.5));

  const meta = [];
  if (type.questionCount) {
    meta.push(type.screening ? `Up to ${type.questionCount} questions` : `${type.questionCount} question${type.questionCount === 1 ? '' : 's'}`);
  }
  meta.push(`~${estMins} min`);

  const produces = isBundle && type.documentCount
    ? `Generates ${type.documentCount} documents`
    : isBundle && type.screening
      ? 'Routes to the right package based on your answers'
      : `Generates a ${type.displayName} draft`;

  return (
    <div className="canvas-inner animate-fade-in" style={{ maxWidth: 760 }}>
      <button type="button" className="btn ghost sm" onClick={onCancel} style={{ marginBottom: 14, alignSelf: 'flex-start' }}>
        <ArrowLeft className="w-3.5 h-3.5" />
        All intakes
      </button>

      <div className="card" style={{ padding: 22 }}>
        {/* Header */}
        <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 11, flexShrink: 0,
              background: 'var(--surface-purple)', color: 'var(--text-purple)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{type.displayName}</h1>
              {isBundle && <span className="chip purple sq">Bundle</span>}
            </div>
            {type.description && (
              <div className="muted" style={{ fontSize: 13, lineHeight: 1.55, marginTop: 6 }}>{type.description}</div>
            )}
          </div>
        </div>

        {/* What you'll get */}
        <div style={{ marginTop: 20, paddingTop: 18, borderTop: '0.5px solid var(--border-primary)' }}>
          <SectionLabel>What you&apos;ll get</SectionLabel>
          <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: isBundle && type.documents ? 12 : 0 }}>{produces}</div>
          {isBundle && type.documents && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {type.documents.map((d) => (
                <div
                  key={d}
                  className="row"
                  style={{ gap: 8, alignItems: 'center', padding: '6px 8px', borderRadius: 6, background: 'var(--surface-alpha-primary)', fontSize: 12, color: 'var(--text-secondary)' }}
                >
                  <FileText className="w-3.5 h-3.5" style={{ color: 'var(--icon-tertiary)', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d}</span>
                </div>
              ))}
            </div>
          )}
          {meta.length > 0 && (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {meta.map((m) => <span key={m} className="chip gray sq">{m}</span>)}
            </div>
          )}
        </div>

        {/* Context for prefill */}
        <div style={{ marginTop: 20, paddingTop: 18, borderTop: '0.5px solid var(--border-primary)' }}>
          <SectionLabel>
            <span className="row" style={{ gap: 6, alignItems: 'center' }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--text-purple)' }} />
              Add context to pre-fill <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-tertiary)' }}>· optional</span>
            </span>
          </SectionLabel>
          <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 8 }}>
            Paste call notes, an email thread, or a Slack message. We&apos;ll pre-fill what we can — you confirm
            each answer before anything is generated.
          </div>
          <textarea
            className="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Paste any context here… (optional)"
            disabled={starting}
            style={{ width: '100%', minHeight: 120, resize: 'vertical' }}
          />
        </div>

        {/* What you'll be asked — expanded by default; the questionnaire and
            (for screening bundles) the decision chart are the point of this step. */}
        {(sections.length > 0 || type.logicTree) && (
          <div style={{ marginTop: 20, paddingTop: 18, borderTop: '0.5px solid var(--border-primary)' }}>
            <button
              type="button"
              onClick={() => setShowQuestions((v) => !v)}
              className="row"
              style={{ gap: 6, alignItems: 'center', background: 'none', border: 0, cursor: 'pointer', padding: 0, color: 'var(--text-primary)' }}
            >
              {showQuestions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                What you&apos;ll be asked{totalQuestions ? ` (${type.screening ? 'up to ' : ''}${totalQuestions})` : ''}
              </span>
            </button>

            {showQuestions && (
              <div style={{ marginTop: 14 }}>
                <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginBottom: 16 }}>
                  The intake asks these one at a time; conditional branches gray out automatically when they
                  don&apos;t apply.
                </div>
                {type.logicTree && (
                  <div style={{ marginBottom: 18 }}>
                    <SectionLabel>How the screening decides</SectionLabel>
                    <LogicTree tree={type.logicTree} />
                  </div>
                )}
                {sections.map((section, sIdx) => (
                  <div key={section.id} style={{ marginBottom: sIdx < sections.length - 1 ? 16 : 0 }}>
                    {section.label && <SectionLabel>{section.label}</SectionLabel>}
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)' }}>
                      {section.questions.map((q) => <li key={q.field} style={{ marginBottom: 6 }}>{q.label}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 22, paddingTop: 18, borderTop: '0.5px solid var(--border-primary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, flex: '1 1 240px' }}>
            This is a drafting aid — Legal reviews everything before it&apos;s final.
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn ghost" onClick={onCancel} disabled={starting}>Cancel</button>
            <button type="button" className="btn primary" onClick={() => onStart(notes)} disabled={starting}>
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {starting ? 'Starting…' : 'Start intake'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-secondary)', marginBottom: 8 }}>
      {children}
    </div>
  );
}
